<script setup lang="ts">
/**
 * Slideover form for creating and editing tags.
 * Wraps USlideover with name input and ColorSwatchPicker.
 */
import { router } from "@inertiajs/vue3"
import { computed, ref, watch } from "vue"

import ColorSwatchPicker from "@/components/ColorSwatchPicker.vue"
import SlideoverFooter from "@/components/SlideoverFooter.vue"
import { tagPath, tagsPath } from "@/routes"

const props = defineProps<{
  open: boolean
  colors: string[]
  tag: { id: string; name: string; color_class: string } | null
}>()

const emit = defineEmits<{
  "update:open": [value: boolean]
}>()

const name = ref("")
const colorClass = ref("gray")
const saving = ref(false)
const formErrors = ref<Record<string, string>>({})

const isEdit = computed(() => !!props.tag)
const title = computed(() => (isEdit.value ? "Edit Tag" : "New Tag"))
const submitLabel = computed(() => (isEdit.value ? "Save" : "Create Tag"))

watch(
  () => props.open,
  (open) => {
    if (open) {
      name.value = props.tag?.name ?? ""
      colorClass.value = props.tag?.color_class ?? "gray"
      formErrors.value = {}
    }
  },
)

function close() {
  emit("update:open", false)
}

function submit() {
  saving.value = true
  const data = { tag: { name: name.value, color_class: colorClass.value } }

  if (isEdit.value) {
    router.patch(tagPath({ id: props.tag!.id }), data, {
      onSuccess: () => close(),
      onError: (errors) => (formErrors.value = errors),
      onFinish: () => (saving.value = false),
    })
  } else {
    router.post(tagsPath(), data, {
      onError: (errors) => (formErrors.value = errors),
      onFinish: () => (saving.value = false),
    })
  }
}
</script>

<template>
  <USlideover :open="open" :title="title" @update:open="emit('update:open', $event)">
    <template #body>
      <form id="tag-form" class="flex flex-col gap-6" @submit.prevent="submit">
        <UFormField label="Name" required :error="formErrors.name">
          <UInput v-model="name" placeholder="e.g. Camping Gear" maxlength="50" autofocus />
        </UFormField>

        <UFormField label="Color">
          <ColorSwatchPicker v-model="colorClass" :colors="colors" />
        </UFormField>
      </form>
    </template>

    <template #footer>
      <SlideoverFooter
        form-id="tag-form"
        :submit-label="submitLabel"
        :loading="saving"
        @cancel="close"
      />
    </template>
  </USlideover>
</template>
